import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Activity, Search, RefreshCw, ChevronLeft, ChevronRight,
  Eye, Users, FileText, CreditCard, MessageSquare,
  Shield, Settings, Package, Clock, Landmark
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_CONFIG = {
  user: { icon: Users, color: 'bg-blue-100 text-blue-700', label: 'User' },
  role: { icon: Shield, color: 'bg-purple-100 text-purple-700', label: 'Role' },
  loan: { icon: CreditCard, color: 'bg-emerald-100 text-emerald-700', label: 'Loan' },
  borrower: { icon: Users, color: 'bg-cyan-100 text-cyan-700', label: 'Borrower' },
  product: { icon: Package, color: 'bg-amber-100 text-amber-700', label: 'Product' },
  payment: { icon: Landmark, color: 'bg-green-100 text-green-700', label: 'Payment' },
  disbursement: { icon: Landmark, color: 'bg-teal-100 text-teal-700', label: 'Disbursement' },
  application: { icon: FileText, color: 'bg-indigo-100 text-indigo-700', label: 'Application' },
  message: { icon: MessageSquare, color: 'bg-sky-100 text-sky-700', label: 'Message' },
  template: { icon: FileText, color: 'bg-rose-100 text-rose-700', label: 'Template' },
  messaging_config: { icon: Settings, color: 'bg-orange-100 text-orange-700', label: 'Msg Config' },
  settings: { icon: Settings, color: 'bg-slate-100 text-slate-700', label: 'Settings' },
  ptp: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'PTP' },
};

const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  status_change: 'Status Changed',
  role_change: 'Role Changed',
  profile_update: 'Profile Updated',
  password_reset: 'Password Reset',
  kyc_update: 'KYC Updated',
  risk_flag_added: 'Risk Flag',
  scoring_update: 'Score Updated',
  collections_assigned: 'Collections Assigned',
  dunning_update: 'Dunning Updated',
  seo_updated: 'SEO Updated',
  config_update: 'Config Updated',
  send: 'Sent',
};

function formatChanges(changes) {
  if (!changes || typeof changes !== 'object') return null;
  const entries = Object.entries(changes).filter(([k, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return null;
  return entries;
}

export default function ActivityLog({ getAuthHeader }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, log: null });
  const PAGE_SIZE = 50;

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (search) params.set('search', search);
      if (entityFilter !== 'all') params.set('entity_type', entityFilter);
      if (actionFilter !== 'all') params.set('action', actionFilter);
      if (actorFilter !== 'all') params.set('performed_by', actorFilter);
      const res = await axios.get(`${API_URL}/audit-logs?${params}`, getAuthHeader());
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { toast.error('Failed to load activity log'); }
  }, [page, search, entityFilter, actionFilter, actorFilter, getAuthHeader]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/audit-logs/summary`, getAuthHeader());
      setSummary(res.data);
    } catch (e) { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-indigo-50"><Activity className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Total Events</p>
                <p className="text-xl font-bold tabular-nums" data-testid="audit-total">{summary?.total ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-50"><Clock className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Today</p>
                <p className="text-xl font-bold tabular-nums" data-testid="audit-today">{summary?.today ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-50"><Users className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Active Actors</p>
                <p className="text-xl font-bold tabular-nums">{summary?.actors?.length ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 px-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-50"><Package className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Entity Types</p>
                <p className="text-xl font-bold tabular-nums">{summary?.entity_types?.length ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base">Activity Log</CardTitle>
              <CardDescription>Complete audit trail of all actions across the platform</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchLogs(); fetchSummary(); }} data-testid="audit-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input placeholder="Search actions, actors, IDs..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="audit-search" />
            </div>
            <Select value={entityFilter} onValueChange={v => { setEntityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="audit-entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {(summary?.entity_types || []).map(t => (
                  <SelectItem key={t} value={t}>{ENTITY_CONFIG[t]?.label || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={v => { setActionFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="audit-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {(summary?.actions || []).map(a => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actorFilter} onValueChange={v => { setActorFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="audit-actor-filter"><SelectValue placeholder="Actor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actors</SelectItem>
                {(summary?.actors || []).map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No activity found</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                {logs.map(log => {
                  const ec = ENTITY_CONFIG[log.entity_type] || { icon: Activity, color: 'bg-slate-100 text-slate-700', label: log.entity_type };
                  const Icon = ec.icon;
                  const changes = formatChanges(log.changes);
                  return (
                    <div
                      key={log.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer border border-transparent hover:border-slate-200"
                      onClick={() => setDetailDialog({ open: true, log })}
                      data-testid={`audit-row-${log.id}`}
                    >
                      <div className={`p-2 rounded-lg shrink-0 ${ec.color.split(' ')[0]}`}>
                        <Icon className={`w-4 h-4 ${ec.color.split(' ')[1]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-[10px] ${ec.color}`}>{ec.label}</Badge>
                          <span className="font-medium text-sm">{ACTION_LABELS[log.action] || log.action}</span>
                          {changes && changes.length <= 2 && (
                            <span className="text-xs text-slate-500 truncate">
                              {changes.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 30) : String(v).slice(0, 30)}`).join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                          <span>by <span className="text-slate-600">{log.performed_by}</span></span>
                          <span className="text-slate-300">|</span>
                          <span className="tabular-nums">{formatDate(log.performed_at)}</span>
                          {log.entity_id && <><span className="text-slate-300">|</span><span className="font-mono text-[10px]">{log.entity_id.slice(0, 8)}...</span></>}
                        </div>
                      </div>
                      <Eye className="w-4 h-4 text-slate-300 shrink-0" />
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-xs text-slate-500">{total} events total</p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1.5 text-sm tabular-nums">{page + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={o => setDetailDialog({ open: o, log: o ? detailDialog.log : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Activity Detail</DialogTitle></DialogHeader>
          {detailDialog.log && (() => {
            const log = detailDialog.log;
            const ec = ENTITY_CONFIG[log.entity_type] || { color: 'bg-slate-100 text-slate-700', label: log.entity_type };
            const changes = formatChanges(log.changes);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500 text-xs block">Entity</span>
                    <Badge className={ec.color}>{ec.label}</Badge>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs block">Action</span>
                    <p className="font-medium">{ACTION_LABELS[log.action] || log.action}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs block">Performed By</span>
                    <p className="font-medium">{log.performed_by}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs block">Date &amp; Time</span>
                    <p className="font-medium text-xs">{formatDate(log.performed_at)}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500 text-xs block">Entity ID</span>
                    <p className="font-mono text-xs bg-slate-50 px-2 py-1 rounded">{log.entity_id}</p>
                  </div>
                </div>
                {changes && (
                  <div>
                    <span className="text-slate-500 text-xs block mb-2">Changes / Details</span>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                      {changes.map(([key, val]) => (
                        <div key={key} className="flex items-start gap-2 text-sm">
                          <span className="text-slate-500 min-w-[100px] shrink-0">{key}:</span>
                          <span className="font-medium break-all">
                            {typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
